import { useParams } from "react-router-dom";
import { useToast } from "../../../../framework/components/bwj-toast/context";
import * as formService from '../../form-services/handle-editor';
import { MessageBarType, Text } from "@fluentui/react";
import { useState, useMemo, useCallback, useEffect } from "react";
import { IProductTexture } from "../../../../entities-generated/common/product-texture";
import { IStormDoorHandleTypeOption } from "../../../../entities-generated/common/storm-door-handle-type-option";
import { BwjCheckbox } from "../../../../framework/components/bwj-checkbox";
import { BwjImageBrowser } from "../../../../framework/components/bwj-image-browser";
import { BwjGrid, Row, Col } from "../../../../framework/components/bwj-layout";
import { Spacing } from "../../../../framework/components/bwj-layout/styles";
import { BwjPageContent } from "../../../../framework/components/bwj-page-content";
import { BwjPageHeader } from "../../../../framework/components/bwj-page-header";
import { BwjSingleSelectField } from "../../../../framework/components/bwj-select-field/single";
import { BwjTextField } from "../../../../framework/components/bwj-text-field";
import { BwjViewState, BwjViewManager } from "../../../../framework/components/bwj-view-manager";
import { replaceFirstWhere } from "../../../../framework/utils/collection";
import { AppRoutes } from "../../../app-routes";
import { ProductTextureEditingModel } from "../../../entities/product-texture-editing-model";
import { ProductVariantEditorPanel } from "../../../storm-door/components/variant-editor";
import { TextureVariantListItem } from "../../../storm-door/components/variant-listing";
import { IStormDoorHandleEditingModel, StormDoorHandleEditingModel } from "../../../../entities-generated/models/storm-door/storm-door-handle-editing-model";
import { StormDoorHandleApiClient } from "../../../../services/storm-door-handle";
import { StormDoorColor } from "../../../../entities-generated/common/storm-door-color";
import { BwjLazyImage } from "../../../../framework/components/bwj-lazy-image";

const subscriberId = 'Page.EditStormHandle';

const colorOptions = [{
  id: StormDoorColor.Indeterminate,
  description: 'Not applicable'
}];

export const PageEditStormHandle: React.FunctionComponent = () => {
  const { id: handleId } = useParams<{id: string}>();
  const { showToast } = useToast();
  const formModel = formService.useFormModel(subscriberId);
  const hasUnsavedChanges = formService.useHasUnsavedChanges(formModel, true);
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const [handle, setHandle] = useState<IStormDoorHandleEditingModel>(new StormDoorHandleEditingModel());
  const [auxImagesDisplayIndex, setAuxImagesDisplayIndex] = useState(0);
  const [variantEditorArgs, setVariantEditorArgs] = useState<ProductTextureEditingModel | undefined>(undefined);
  const productBaseUrl = useMemo(() => `${handle.productUrl.replace(/\/[a-z0-9]+$/, '')}`, [handle]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const auxImages = useMemo(() => formService.imageUrls.getOutputValue(), [formModel]);

  const initializeView = useCallback(() => {
    StormDoorHandleApiClient.getStormDoorHandleForEditing(handleId)
    .then(d => {
      setHandle(d);
      formService.initializeService(d);
      setViewState(BwjViewState.Ready);
    })
    .catch((e) => {
      console.log(e);
      setViewState(BwjViewState.Error);
    });
  }, [handleId]);
  
  useEffect(() => {
    initializeView();
  }, [initializeView]);

  const onUpdateHandle = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid === false) { return null; }
      
      setViewState(BwjViewState.Loading);
      const request = formService.toUpdateRequest();
      return StormDoorHandleApiClient.updateStormDoorHandle(request);
    })
    .then(resp => {
      if(!resp) { return; }
      showToast(MessageBarType.success, `Handle '${formModel.description.value}' successfully updated`);
      return initializeView();
    })
    .catch(() => {
      showToast(MessageBarType.error, 'An unexpected error occurred');
      setViewState(BwjViewState.Ready);
    });
  }

  const onViewOnVendorSite = () => {
    window.open(`${handle.productUrl}`, '_blank');
  }

  const onVariantSelected = (variant: IProductTexture) => {
    const args = new ProductTextureEditingModel(variant, colorOptions);
    setVariantEditorArgs(args);
  }

  const onUpdateVariant = (variant: IProductTexture) => {
    const variants = replaceFirstWhere(formModel.textureVariants, v => v.id === variant.id, variant);
    formService.textureVariants.onUpdate(variants);
  }

  const onViewVariantOnVendorSite = (variant: IProductTexture) => {
    window.open(`${productBaseUrl}/${variant.id}`, '_blank');
  }

  return (
    <>
    <ProductVariantEditorPanel
      invokePanelDisplay={variantEditorArgs}
      onUpdateVariant={onUpdateVariant}
    />
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: 'Loading handle...'
      }}
      errorState={{
        message: 'An unexpected error occurred'
      }}
    >
      <BwjPageHeader
        title={handle.description}
        titleIconName={AppRoutes.editStormDoor.icon}
        actionButtons={[{
          id: 'btnUpdate',
          name: 'Save changes',
          iconName: 'Save',
          disabled: hasUnsavedChanges === false,
          onClick: onUpdateHandle,
        }, {
          id: 'btnViewAtVendor',
          name: 'View on vendor site',
          iconName: 'Globe',
          onClick: onViewOnVendorSite,
        }]}
      />
      <BwjPageContent contentDescription={'primary content (no subcontent)'}>
        <BwjGrid>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={3}>
              <BwjTextField
                id={formService.brand.id}
                required={true}
                label={formService.brand.description}
                onUpdate={formService.brand.onUpdate}
                input={formModel.brand}
              />
            </Col>
            <Col small={12} large={9}>
              <BwjTextField
                id={formService.description.id}
                required={true}
                label={formService.description.description}
                onUpdate={formService.description.onUpdate}
                input={formModel.description}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={3}>
              <BwjCheckbox
                id={formService.isActive.id}
                label={formService.isActive.description}
                checked={formModel.isActive}
                onChange={formService.isActive.onUpdate}
              />
            </Col>
            <Col small={12} large={3}>
              <BwjCheckbox
                id={formService.isNotAvailable.id}
                label={formService.isNotAvailable.description}
                checked={formModel.isNotAvailable}
                onChange={formService.isNotAvailable.onUpdate}
              />
            </Col>
            <Col small={12} large={6}>
              <BwjSingleSelectField<IStormDoorHandleTypeOption>
                id={formService.handleType.id}
                required={true}
                label={formService.handleType.description}
                onUpdate={formService.handleType.onUpdate}
                input={formModel.handleType}
                options={formService.handleType.options}
                optionDisplayKey={'description'}
                optionValueKey={'id'}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={4}>
              <BwjTextField
                id={formService.price.id}
                required={true}
                label={formService.price.description}
                onUpdate={formService.price.onUpdate}
                input={formModel.price}
                filter={/^[0-9.]*$/}
              />
            </Col>
            <Col small={12} large={4}>
              <BwjTextField
                id={formService.inStoreQuantity.id}
                required={true}
                label={formService.inStoreQuantity.description}
                onUpdate={formService.inStoreQuantity.onUpdate}
                input={formModel.inStoreQuantity}
                filter={/^[0-9]*$/}
              />
            </Col>
            <Col small={12} large={4}>
              <BwjTextField
                id={formService.pickupLeadTimeInDays.id}
                required={true}
                label={formService.pickupLeadTimeInDays.description}
                onUpdate={formService.pickupLeadTimeInDays.onUpdate}
                input={formModel.pickupLeadTimeInDays}
                filter={/^[0-9]*$/}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={6}>
              <BwjTextField
                id={formService.imageUrl.id}
                required={false}
                label={formService.imageUrl.description}
                onUpdate={formService.imageUrl.onUpdate}
                input={formModel.imageUrl}
              />
            </Col>
            <Col small={12} large={6}>
              <div style={{ height: 500 }}>
                <BwjLazyImage
                  src={formModel.imageUrl.value}
                  loadImage={true}
                  styles={{root: {width: 400}}}
                />
              </div>
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={6}>
              <BwjImageBrowser
                id={`${formService.imageUrls.id}-image-viewer`}
                images={auxImages.map(x => { return { src: x }; })}
                displayIndex={auxImagesDisplayIndex}
                loadImages={true}
                imageWidth={400}
                onDisplayIndexChanged={index => setAuxImagesDisplayIndex(index)}
              />
            </Col>
            <Col small={12} large={6}>
              <BwjTextField
                id={formService.imageUrls.id}
                required={false}
                label={formService.imageUrls.description}
                onUpdate={formService.imageUrls.onUpdate}
                input={formModel.imageUrls}
                multiline={true}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              <BwjTextField
                id={formService.sellingPoints.id}
                required={false}
                label={formService.sellingPoints.description}
                onUpdate={formService.sellingPoints.onUpdate}
                input={formModel.sellingPoints}
                multiline={true}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              {formModel.textureVariants.map(variant =>
              <TextureVariantListItem
                key={variant.id}
                variant={variant}
                onClick={onVariantSelected}
                onGoToVendorSite={onViewVariantOnVendorSite}
              />)}
            </Col>
          </Row>
        </BwjGrid>
      </BwjPageContent>
    </BwjViewManager>
    </>
  );
};